import {TranslationKeys, getNestedTranslation, TranslationObject} from '@colonist/i18n'
import {ColonistOneTimePurchaseType, LobbyImage} from '@colonist/types'
import {BaseColonistStoreProduct, StoreProductType, StripeCheckoutTypes} from './BaseColonistStoreProduct'
import * as types from '@colonist/types'

export enum MobileMembershipIAPSKUIds {
    EliteYearly = 'elite_yearly',
    EliteMonthly = 'elite_monthly',
    Holiday2023AllAccessPass = 'all_access_pass_holiday_2023',
    PremiumYearly = 'premium_yearly',
    PremiumMonthly = 'premium_monthly',
    PlusYearly = 'plus_yearly',
    PlusMonthly = 'plus_monthly',
}

export enum MobileMembershipGiftIAPSKUIds {
    EliteYearly = 'elite_yearly_gift',
    EliteMonthly = 'elite_monthly_gift',
    PremiumYearly = 'premium_yearly_gift',
    PremiumMonthly = 'premium_monthly_gift',
    PlusYearly = 'plus_yearly_gift',
    PlusMonthly = 'plus_monthly_gift',
}

export class ColonistMembershipPerk {
    type: ColonistMembershipPerkType
    description: TranslationObject
    imageUrl: LobbyImage
}

export class PerkAllMaps extends ColonistMembershipPerk {
    type = ColonistMembershipPerkType.AllMaps
    description: TranslationObject = {key: 'strings:storePage.sections.membership.perks.allMaps'}
    imageUrl = LobbyImage.CheckMark
}

export class PerkAllExpansions extends ColonistMembershipPerk {
    type = ColonistMembershipPerkType.AllExpansions
    description: TranslationObject = {key: 'strings:storePage.sections.membership.perks.allExpansions'}
    imageUrl = LobbyImage.CheckMark
}

export class PerkRankedGames extends ColonistMembershipPerk {
    type = ColonistMembershipPerkType.PremiumMatchmaking
    description: TranslationObject = {key: 'strings:storePage.sections.membership.perks.premiumMatchmaking.title'}
    imageUrl = LobbyImage.CheckMark
}

export class PerkCustomLinks extends ColonistMembershipPerk {
    type = ColonistMembershipPerkType.CustomLinks
    description: TranslationObject = {key: 'strings:storePage.sections.membership.perks.customLinks'}
    imageUrl = LobbyImage.CheckMark
}

export class PerkColonistCoins extends ColonistMembershipPerk {
    type = ColonistMembershipPerkType.ColonistCoins
    description: TranslationObject = {key: 'strings:storePage.sections.membership.perks.colonistCoinsPerMonth'}
    imageUrl = LobbyImage.CheckMark
}

export class PerkNoAds extends ColonistMembershipPerk {
    type = ColonistMembershipPerkType.NoAds
    description: TranslationObject = {key: 'strings:storePage.sections.membership.perks.noAdsAndFullScreen'}
    imageUrl = LobbyImage.CheckMark
}

export class PerkAllColors extends ColonistMembershipPerk {
    type = ColonistMembershipPerkType.AllColors
    description: TranslationObject = {key: 'strings:storePage.sections.membership.perks.allColors'}
    imageUrl = LobbyImage.CheckMark
}

export class PerkBasicReplay extends ColonistMembershipPerk {
    type = ColonistMembershipPerkType.BasicReplay
    description: TranslationObject = {key: 'strings:storePage.sections.membership.perks.basicReplay'}
    imageUrl = LobbyImage.CheckMark
}

export class PerkFullReplay extends ColonistMembershipPerk {
    type = ColonistMembershipPerkType.FullReplay
    description: TranslationObject = {key: 'strings:storePage.sections.membership.perks.fullReplay'}
    imageUrl = LobbyImage.CheckMark
}

/**
 * @deprecated Import ColonistMembershipType from @colonist/types
 */
export import ColonistMembershipType = types.ColonistMembershipType
import {ColonistMembershipPerkType} from '@colonist/lobby-types'

export class ColonistMembershipProduct implements BaseColonistStoreProduct {
    // BaseColonistStoreProduct
    readonly storeProductType: StoreProductType = StoreProductType.Membership
    readonly type: ColonistMembershipType
    readonly stripeCheckoutType: StripeCheckoutTypes = StripeCheckoutTypes.Subscription
    readonly itemQuantity: number = 1
    readonly discordSKUId: string | undefined
    // BaseColonistStoreProduct
    readonly lobbyImage: LobbyImage
    readonly price: number
    readonly discount: number
    readonly membershipTitle: TranslationObject
    readonly membershipColor: string
    readonly perks: ColonistMembershipPerk[]
    readonly billedAnnually: boolean
    readonly mobileIAPSKUId: MobileMembershipIAPSKUIds | MobileMembershipGiftIAPSKUIds

    readonly bestValue: boolean
    readonly isActive: () => boolean

    oneTimePurchaseType: ColonistOneTimePurchaseType | undefined = undefined

    constructor(source: Pick<ColonistMembershipProduct, 'type' | 'discordSKUId' | 'lobbyImage' | 'price' | 'discount' | 'membershipTitle' | 'membershipColor' | 'perks' | 'billedAnnually' | 'bestValue' | 'isActive' | 'mobileIAPSKUId'>) {
        Object.assign(this, source)
    }

    // BaseColonistStoreProduct
    getProductName(): TranslationObject {
        const membershipBody: TranslationObject = {key: 'strings:storePage.membership.body'}
        const frequencyKey: TranslationKeys = this.billedAnnually ? 'strings:storePage.membership.frequency.annual' : 'strings:colonistCommon.utils.emptyString'
        const nestedTranslations = {
            title: this.membershipTitle,
            frequency: {key: frequencyKey},
        }
        const membershipTranslation = getNestedTranslation(membershipBody, nestedTranslations)
        return membershipTranslation
    }

    getDefaultPrice(): string {
        return `${this.price / 100}`
    }

    getAnnualPrice(): string {
        return `${(this.price * 12) / 100}`
    }

    static getMonthlyPriceOfAnnualMembership(membershipType: ColonistMembershipType): string {
        switch(membershipType) {
            case ColonistMembershipType.PlusYearly: return ColonistMembershipProduct.getMembershipProduct(ColonistMembershipType.PlusMonthly).getDefaultPrice()
            case ColonistMembershipType.PremiumYearly: return ColonistMembershipProduct.getMembershipProduct(ColonistMembershipType.PremiumMonthly).getDefaultPrice()
            case ColonistMembershipType.EliteYearly: return ColonistMembershipProduct.getMembershipProduct(ColonistMembershipType.EliteMonthly).getDefaultPrice()
            default: throw new Error(`No monthly price found for annual membership type: ${membershipType}`)
        }
    }
    // BaseColonistStoreProduct

    static isAnnualMembership(membershipType: ColonistMembershipType): boolean {
        switch(membershipType) {
            case ColonistMembershipType.PlusYearly:
            case ColonistMembershipType.PremiumYearly:
            case ColonistMembershipType.EliteYearly:
                return true
            default:
                return false
        }
    }

    static getAllMembershipProducts(): ColonistMembershipProduct[] {
        return Array.from(this.products.values()).filter((product) => product.oneTimePurchaseType == null)
    }

    // TODO: should override in ColonistGiftMembershipProduct to return ColonistOneTimePurchaseProduct
    static getMembershipProduct(membershipType: ColonistMembershipType): ColonistMembershipProduct {
        return this.products.get(membershipType) as ColonistMembershipProduct
    }

    protected static readonly products = new Map<ColonistMembershipType, ColonistMembershipProduct>()

    static initProducts(discordSKUIds: Record<string, string>) {
        const holidayProduct = new ColonistMembershipProduct({
            type: ColonistMembershipType.Holiday2023AllAccessPass,
            price: 699,
            discordSKUId: discordSKUIds.passAllAccessHoliday2023,
            mobileIAPSKUId: MobileMembershipIAPSKUIds.Holiday2023AllAccessPass,
            membershipTitle: {key: 'strings:storePage.membership.title.holiday2023'},
            membershipColor: 'membership_type_holiday_2023',
            lobbyImage: LobbyImage.StoreMembershipHoliday2023,
            perks: [new PerkRankedGames(), new PerkNoAds(), new PerkAllExpansions(), new PerkAllMaps()],
            billedAnnually: false,
            bestValue: false,
            discount: 0,
            isActive: () => false,
        })
        holidayProduct.oneTimePurchaseType = ColonistOneTimePurchaseType.Holiday2023AllAccessPass
        this.products.set(ColonistMembershipType.Holiday2023AllAccessPass, holidayProduct)

        this.products.set(ColonistMembershipType.PlusMonthly, new ColonistMembershipProduct({
            type: ColonistMembershipType.PlusMonthly,
            price: 899,
            discordSKUId: discordSKUIds.membershipPlusMonthly,
            mobileIAPSKUId: MobileMembershipIAPSKUIds.PlusMonthly,
            membershipTitle: {key: 'strings:storePage.membership.title.plus'},
            membershipColor: 'membership_type1',
            lobbyImage: LobbyImage.StoreMembershipPlus,
            perks: [new PerkRankedGames(), new PerkNoAds(), new PerkBasicReplay()],
            billedAnnually: false,
            bestValue: false,
            discount: 0,
            isActive: () => true,
        }))

        this.products.set(ColonistMembershipType.PremiumMonthly, new ColonistMembershipProduct({
            type: ColonistMembershipType.PremiumMonthly,
            price: 1899,
            discordSKUId: discordSKUIds.membershipPremiumMonthly,
            mobileIAPSKUId: MobileMembershipIAPSKUIds.PremiumMonthly,
            membershipTitle: {key: 'strings:storePage.membership.title.premium'},
            membershipColor: 'membership_type2',
            lobbyImage: LobbyImage.StoreMembershipPremium,
            perks: [new PerkRankedGames(), new PerkNoAds(), new PerkFullReplay(), new PerkAllExpansions(), new PerkAllMaps()],
            billedAnnually: false,
            bestValue: true,
            discount: 0,
            isActive: () => true,
        }))

        this.products.set(ColonistMembershipType.EliteMonthly, new ColonistMembershipProduct({
            type: ColonistMembershipType.EliteMonthly,
            price: 2499,
            discordSKUId: discordSKUIds.membershipEliteMonthly,
            mobileIAPSKUId: MobileMembershipIAPSKUIds.EliteMonthly,
            membershipTitle: {key: 'strings:storePage.membership.title.elite'},
            membershipColor: 'membership_type3',
            lobbyImage: LobbyImage.StoreMembershipElite,
            perks: [new PerkRankedGames(), new PerkNoAds(), new PerkFullReplay(), new PerkAllExpansions(), new PerkAllMaps(), new PerkAllColors()],
            billedAnnually: false,
            bestValue: false,
            discount: 0,
            isActive: () => true,
        }))

        this.products.set(ColonistMembershipType.PlusYearly, new ColonistMembershipProduct({
            type: ColonistMembershipType.PlusYearly,
            price: 699,
            discordSKUId: discordSKUIds.membershipPlusYearly,
            mobileIAPSKUId: MobileMembershipIAPSKUIds.PlusYearly,
            membershipTitle: {key: 'strings:storePage.membership.title.plus'},
            membershipColor: 'membership_type1',
            lobbyImage: LobbyImage.StoreMembershipPlus,
            perks: [new PerkRankedGames(), new PerkNoAds(), new PerkBasicReplay()],
            billedAnnually: true,
            bestValue: false,
            discount: 24,
            isActive: () => true,
        }))

        this.products.set(ColonistMembershipType.PremiumYearly, new ColonistMembershipProduct({
            type: ColonistMembershipType.PremiumYearly,
            price: 1499,
            discordSKUId: discordSKUIds.membershipPremiumYearly,
            mobileIAPSKUId: MobileMembershipIAPSKUIds.PremiumYearly,
            membershipTitle: {key: 'strings:storePage.membership.title.premium'},
            membershipColor: 'membership_type2',
            lobbyImage: LobbyImage.StoreMembershipPremium,
            perks: [new PerkRankedGames(), new PerkNoAds(), new PerkFullReplay(), new PerkAllExpansions(), new PerkAllMaps()],
            billedAnnually: true,
            bestValue: true,
            discount: 48,
            isActive: () => true,
        }))

        this.products.set(ColonistMembershipType.EliteYearly, new ColonistMembershipProduct({
            type: ColonistMembershipType.EliteYearly,
            price: 1999,
            discordSKUId: discordSKUIds.membershipEliteYearly,
            mobileIAPSKUId: MobileMembershipIAPSKUIds.EliteYearly,
            membershipTitle: {key: 'strings:storePage.membership.title.elite'},
            membershipColor: 'membership_type3',
            lobbyImage: LobbyImage.StoreMembershipElite,
            perks: [new PerkRankedGames(), new PerkNoAds(), new PerkFullReplay(), new PerkAllExpansions(), new PerkAllMaps(), new PerkAllColors()],
            billedAnnually: true,
            bestValue: false,
            discount: 60,
            isActive: () => true,
        }))
    }
}
